// Analysis pages shared by the social-media return parsers (Snapchat warrant,
// Facebook, Instagram). Each parser turns its parsed return into one `input`
// (see socialNav) and renders <SocialAnalysis page=… input=… /> for the
// analysis entries in its sidebar. Everything is calculated in the browser.
//
// input = {
//   source: 'Snap', title: 'Snapchat Warrant Return', scope: '@user',
//   events: [{ t, account, kind, source, activity, ips, handle, direction, service, thread?, places?, detail }],
//   ownKeys: Set of the account's own handle keys,
//   connected?: { label: 'friends', size, match(handle) },  // people messaged who aren't connected
//   people?: { title, sub, explain, rows: [{ t, who, what, detail, flag? }] },
//   changes: [{ t, type, detail, ip? }],                   // account changes; nearest IP inferred if missing
//   places?: [{ t, lat, lon, source, detail }],
//   profile: [[label, value]],                              // for the PDF
// }

import React, { useState, useMemo } from 'react';
import {
  useT, useTimeFormat, TIME_ZONES, Heading, Note, Explain, InfoCard, Chip, DataTable, GeneratedBadge, GeneratedLegend,
  selectStyle, btnStyle,
} from './ui';
import {
  PatternOfLifeView, IncidentView, ContactsView, IpAnalysis, SecurityView, CaseSummaryView, HomeWorkCards,
  summaryDoc, standardSections, downloadPdf, ipHistory,
} from './views';
import { contactRanking, newIpChecker, nightWorkProfile, homeAndWork } from '../../utils/analysisKit';

const PREFIX = 'an-';
export const isAnalysisPage = key => String(key || '').startsWith(PREFIX);

// Sidebar entries for the analysis pages this return has data for.
export function socialNav(input) {
  if (!input) return [];
  const hasIps = input.events.some(e => e.ips && e.ips.length);
  const msgs = input.events.filter(e => e.thread);
  return [
    { key: 'pattern', label: 'Pattern of life' },
    { key: 'incident', label: 'Incident window' },
    { key: 'contacts', label: 'Contacts by day' },
    msgs.length && { key: 'threads', label: 'Conversations over time' },
    input.connected && input.connected.size > 0 && { key: 'strangers', label: `Messaged, not ${input.connected.label}` },
    input.people && input.people.rows.length && { key: 'people', label: input.people.title, count: input.people.rows.length },
    input.places && input.places.length && { key: 'places', label: 'Places (GPS)', count: input.places.length },
    hasIps && { key: 'ips', label: 'IP analysis' },
    { key: 'changes', label: 'Account changes', count: input.changes.length || undefined },
    { key: 'summary', label: 'Case summary (PDF)' },
  ].filter(Boolean).map(x => ({ ...x, key: PREFIX + x.key }));
}

// Account changes with the IP they came from. When the return doesn't log one,
// the IP of the nearest activity within 2 hours is used and marked as inferred.
function changeRows(input) {
  const withIp = input.events.filter(e => e.ips && e.ips.length).sort((a, b) => a.t - b.t);
  const check = newIpChecker(input.events);
  return input.changes.filter(c => c.t).map(c => {
    let ip = c.ip || '', ipInferred = false;
    if (!ip && withIp.length) {
      let best = null;
      for (const e of withIp) if (Math.abs(e.t - c.t) <= 2 * 3600000 && (!best || Math.abs(e.t - c.t) < Math.abs(best.t - c.t))) best = e;
      if (best) { ip = best.ips[0]; ipInferred = true; }
    }
    const account = c.account || input.events[0]?.account || '';
    const r = check(account, ip, c.t);
    const creation = /created|registered|signed up/i.test(c.type);
    return { ...c, account, ip, ipInferred, seen: r.seen, newIp: !!(ip && !creation && r.newIp) };
  }).sort((a, b) => a.t - b.t);
}

// Messages per conversation: when it started and ended, how many days, sent and received.
function threadSummary(events) {
  const map = new Map();
  for (const e of events) {
    if (!e.thread) continue;
    if (!map.has(e.thread)) map.set(e.thread, { thread: e.thread, count: 0, sent: 0, received: 0, first: e.t, last: e.t, days: new Set(), who: new Set(), perMonth: new Map() });
    const x = map.get(e.thread);
    x.count++;
    if (e.direction === 'outgoing') x.sent++; else x.received++;
    x.first = Math.min(x.first, e.t);
    x.last = Math.max(x.last, e.t);
    x.days.add(new Date(e.t).toISOString().slice(0, 10));
    if (e.handle) x.who.add(e.handle);
    const mo = new Date(e.t).toISOString().slice(0, 7);
    x.perMonth.set(mo, (x.perMonth.get(mo) || 0) + 1);
  }
  return [...map.values()].map(x => ({ ...x, days: x.days.size, who: [...x.who], perMonth: x.perMonth }));
}

const placeKey = p => `${Number(p.lat).toFixed(3)}, ${Number(p.lon).toFixed(3)}`;

function downloadKml(name, places, fmt) {
  const esc = s => String(s ?? '').replace(/[<>&"]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]));
  const kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2"><Document><name>${esc(name)}</name>
<description>${esc('Generated by LE Tech Tools from the provider return. Not part of the original legal return.')}</description>
${places.map(p => `<Placemark><name>${esc(fmt(p.t))}</name><description>${esc([p.source, p.detail].filter(Boolean).join(' - '))}</description><TimeStamp><when>${new Date(p.t).toISOString()}</when></TimeStamp><Point><coordinates>${p.lon},${p.lat},0</coordinates></Point></Placemark>`).join('\n')}
</Document></kml>`;
  const url = URL.createObjectURL(new Blob([kml], { type: 'application/vnd.google-earth.kml+xml' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name.replace(/[^\w.-]+/g, '_')}.kml`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function SocialAnalysis({ page, input }) {
  const t = useT();
  const [tz, setTz] = useState('local');
  const [pdfBusy, setPdfBusy] = useState(false);
  const [selThread, setSelThread] = useState(null);
  const fmt = useTimeFormat(tz);
  const tzLabel = tz === 'local' ? 'your time zone' : (TIME_ZONES.find(z => z[0] === tz) || [tz, tz])[1];
  const id = String(page || '').slice(PREFIX.length);
  const { events } = input;

  const own = useMemo(() => new Map([[events[0]?.account, input.ownKeys || new Set()]]), [events, input.ownKeys]);
  const contacts = useMemo(() => contactRanking(events, own, e => e.service || e.source, tz), [events, own, tz]);
  const threads = useMemo(() => threadSummary(events), [events]);
  const strangers = useMemo(() => (input.connected ? contacts.filter(c => !c.own && !input.connected.match(c.handle) && !c.handle.startsWith('Group:')) : []), [contacts, input.connected]);
  const changes = useMemo(() => changeRows(input), [input]);
  const placeEvents = useMemo(() => (input.places || []).map(p => ({ ...p, account: events[0]?.account, activity: true, key: placeKey(p) })), [input.places, events]);
  const placeProfile = useMemo(() => nightWorkProfile(placeEvents, tz, e => [e.key]), [placeEvents, tz]);
  const placeHomeWork = useMemo(() => homeAndWork(placeProfile, x => [x.key, x.key]), [placeProfile]);

  const downloadSummary = async () => {
    setPdfBusy(true);
    try {
      const sections = [{ heading: 'Account', kv: input.profile }];
      sections.push(...standardSections({ events, tz, fmt, contacts, contactLabel: 'Contact' }));
      if (threads.length) {
        sections.push({ heading: 'Conversations', small: 'Messages per conversation in the return.', table: { headers: ['Conversation', 'Messages', 'Sent', 'Days', 'First', 'Last'],
          rows: [...threads].sort((a, b) => b.days - a.days || b.count - a.count).slice(0, 20).map(x => [x.thread, x.count, x.sent, x.days, fmt(x.first), fmt(x.last)]), widths: ['*', 45, 30, 30, 85, 85] } });
      }
      if (strangers.length) {
        sections.push({ heading: `Messaged, but not ${input.connected.label}`, table: { headers: ['Contact', 'Days', 'Messages', 'First', 'Last'],
          rows: [...strangers].sort((a, b) => b.days - a.days).slice(0, 20).map(c => [c.handle, c.days, c.count, fmt(c.first), fmt(c.last)]), widths: ['*', 30, 45, 90, 90] } });
      }
      if (input.people && input.people.rows.length) {
        sections.push({ heading: input.people.title, table: { headers: ['Time', 'Who', 'What', 'Note'],
          rows: input.people.rows.filter(r => r.t).sort((a, b) => b.t - a.t).slice(0, 30).map(r => [fmt(r.t), r.who, r.what, r.flag || '']), widths: [85, '*', 110, 110] } });
      }
      if (placeProfile.length) {
        sections.push({ heading: 'Places (GPS)', small: 'Points grouped to about 100 m. Likely home/work = the place seen on the most nights / weekday working days.',
          lines: [['Likely home', placeHomeWork.home], ['Likely work', placeHomeWork.work]].map(([k, g]) => (g ? `${k}: ${g.label} (${g.nights} nights, ${g.workdays} working days, ${g.count} points)` : `${k}: none seen on 3 or more days.`)),
          table: { headers: ['Place (lat, lon)', 'Points', 'Days', 'First', 'Last'], rows: [...placeProfile].sort((a, b) => b.days - a.days).slice(0, 15).map(p => [p.key, p.count, p.days, fmt(p.first), fmt(p.last)]), widths: ['*', 40, 30, 90, 90] } });
      }
      if (changes.length) {
        sections.push({ heading: 'Account changes', table: { headers: ['Time', 'Change', 'Details', 'IP', 'IP history'],
          rows: changes.slice(0, 40).map(c => [fmt(c.t), c.type, c.detail, c.ip ? `${c.ip}${c.ipInferred ? ' (nearest)' : ''}` : '', ipHistory(c)]), widths: [85, 80, '*', 90, 50] } });
      }
      await downloadPdf(summaryDoc({ title: `${input.title}: Case Summary`, scope: input.scope, fmt, tzLabel, source: input.source, sections }),
        `${input.title} case summary ${new Date().toISOString().slice(0, 10)}.pdf`);
    } finally {
      setPdfBusy(false);
    }
  };

  const sel = selectStyle(t);
  const thread = threads.find(x => x.thread === selThread);
  const months = thread ? [...thread.perMonth.entries()].sort() : [];
  const maxMonth = Math.max(1, ...months.map(([, n]) => n));

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      <div style={{ padding: '20px 28px 40px', display: 'flex', flexDirection: 'column', gap: 16, color: t.text }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5, color: t.muted }}>
          <GeneratedLegend>Analysis page: generated by LE Tech Tools from the records in this return. Not part of the original legal return.</GeneratedLegend>
          <label style={{ marginLeft: 'auto' }}>Show times in{' '}
            <select value={tz} onChange={e => setTz(e.target.value)} style={sel}>
              {TIME_ZONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        </div>

        {id === 'pattern' && (
          <PatternOfLifeView events={events} tz={tz} fmt={fmt} tzLabel={tzLabel}
            explain={`Each square counts things the account did (messages it sent, logins and other logged activity) in that hour of that weekday. Messages it only received aren't counted. Darker means busier.`} />
        )}
        {id === 'incident' && (
          <IncidentView events={events} tz={tz} fmt={fmt} tzLabel={tzLabel} handleLabel="People in contact" placeLabel="GPS points"
            homeWork={{}} explain="Pick the time of the incident and how wide a window to look at; messages sent and received are both included." />
        )}
        {id === 'contacts' && (
          <>
            <Heading title="Contacts by day" sub="Everyone the account exchanged messages with, ranked by how many different days." />
            <GeneratedBadge />
            <ContactsView contacts={contacts} fmt={fmt} tzLabel={tzLabel} label="Contact" showDirection
              note="&ldquo;In / out&rdquo; is messages received from them and sent by the account (sent to a group counts for the group)." />
          </>
        )}
        {id === 'threads' && (
          <>
            <Heading title="Conversations over time" sub="Each conversation: when it started and stopped, how many days it was active, and how much each side wrote." />
            <GeneratedBadge />
            <Explain>
              A conversation that starts or stops suddenly near the incident, or one that is busy on many separate days, is worth
              reading first. Click a row to see its messages per month. The messages themselves are on the conversations page.
            </Explain>
            <DataTable key={tz} rows={threads} exportName="conversations over time" initialSort={{ key: 'days', dir: 'desc' }}
              onRowClick={r => setSelThread(r.thread === selThread ? null : r.thread)} rowKey={r => r.thread} selectedKey={selThread}
              columns={[
                { key: 'thread', label: 'Conversation', maxWidth: 320 },
                { key: 'days', label: 'Days', generated: true, title: 'Different days with messages', sort: r => r.days },
                { key: 'count', label: 'Messages', sort: r => r.count },
                { key: 'sent', label: 'Sent', sort: r => r.sent },
                { key: 'received', label: 'Received', sort: r => r.received },
                { key: 'first', label: `First (${tzLabel})`, mono: true, text: r => fmt(r.first), sort: r => r.first },
                { key: 'last', label: 'Last', mono: true, text: r => fmt(r.last), sort: r => r.last },
              ]} />
            {thread && (
              <InfoCard title={<>{thread.thread} · messages per month (UTC)<span style={{ color: t.warn }}> ✱</span></>}
                action={<button style={btnStyle(t)} onClick={() => setSelThread(null)}>Close</button>}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 100, overflowX: 'auto' }}>
                  {months.map(([mo, n]) => (
                    <div key={mo} title={`${mo} · ${n}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 34 }}>
                      <div style={{ width: 22, height: Math.max(3, Math.round(n / maxMonth * 75)), background: t.accent, borderRadius: 2 }} />
                      <div style={{ fontSize: 10, color: t.muted }}>{mo.slice(2)}</div>
                    </div>
                  ))}
                </div>
              </InfoCard>
            )}
          </>
        )}
        {id === 'strangers' && input.connected && (
          <>
            <Heading title={`Messaged, but not ${input.connected.label}`} sub={`People the account exchanged messages with who aren't in its ${input.connected.label} list in this return.`} />
            <GeneratedBadge />
            <Explain>
              Someone the account talks to but isn&rsquo;t connected with can be a new contact, a buyer or seller, someone
              using a second account, or a person who was removed from the list. The {input.connected.label} list is as of the
              date the return was produced, so a person removed earlier shows up here too. Matching is by username or ID.
            </Explain>
            <ContactsView contacts={strangers} fmt={fmt} tzLabel={tzLabel} label="Contact" showDirection />
          </>
        )}
        {id === 'people' && input.people && (
          <>
            <Heading title={input.people.title} sub={input.people.sub} />
            <GeneratedBadge />
            <Explain>{input.people.explain}</Explain>
            <DataTable key={tz} rows={input.people.rows} exportName={input.people.title} initialSort={{ key: 't', dir: 'asc' }}
              columns={[
                { key: 't', label: `Time (${tzLabel})`, mono: true, text: r => (r.t ? fmt(r.t) : ''), sort: r => r.t || 0, width: 190 },
                { key: 'who', label: 'Who', mono: true, color: t.cyan },
                { key: 'what', label: 'What' },
                { key: 'detail', label: 'Details' },
                { key: 'flag', label: 'Note', generated: true, render: r => (r.flag ? <Chip tone="warn">{r.flag}</Chip> : '') },
              ]} />
          </>
        )}
        {id === 'places' && (
          <>
            <Heading title="Places (GPS)" sub="Every record in the return with GPS coordinates, and the places that come up most." />
            <GeneratedBadge />
            <Explain>
              Points within about 100 meters are grouped into one place. The place seen on the most different nights
              (22:00&ndash;06:00) is likely near where the person sleeps; the one seen on the most weekdays 09:00&ndash;17:00
              likely near work or school. Coordinates come from the phone and can be off, and a photo saved later can carry the
              location where it was taken. Export a KML file to open the points in Google Earth or your mapping software: this
              page never sends the coordinates anywhere.
            </Explain>
            <HomeWorkCards homeWork={placeHomeWork} fmt={fmt} profile={placeProfile} noun="place" groupNote="points" />
            <DataTable key={'g' + tz} rows={placeProfile} exportName="places grouped" initialSort={{ key: 'days', dir: 'desc' }}
              columns={[
                { key: 'key', label: 'Place (lat, lon)', mono: true, color: t.cyan },
                { key: 'count', label: 'Points', sort: r => r.count },
                { key: 'days', label: 'Days', generated: true, sort: r => r.days },
                { key: 'nights', label: 'Nights', generated: true, sort: r => r.nights },
                { key: 'workdays', label: 'Workdays', generated: true, sort: r => r.workdays },
                { key: 'first', label: `First (${tzLabel})`, mono: true, text: r => fmt(r.first), sort: r => r.first },
                { key: 'last', label: 'Last', mono: true, text: r => fmt(r.last), sort: r => r.last },
                { key: 'sources', label: 'From', text: r => r.sources.join(', ') },
              ]} />
            <Heading title="All GPS points" sub="As they appear in the return.">
              <button style={btnStyle(t)} onClick={() => downloadKml(`${input.title} GPS points`, input.places, fmt)}>Export KML (Google Earth)</button>
            </Heading>
            <DataTable key={'p' + tz} rows={input.places} exportName="GPS points" initialSort={{ key: 't', dir: 'asc' }}
              columns={[
                { key: 't', label: `Time (${tzLabel})`, mono: true, text: r => fmt(r.t), sort: r => r.t, width: 190 },
                { key: 'lat', label: 'Latitude', mono: true },
                { key: 'lon', label: 'Longitude', mono: true },
                { key: 'source', label: 'From' },
                { key: 'detail', label: 'Details' },
              ]} />
          </>
        )}
        {id === 'ips' && (
          <>
            <Heading title="IP analysis" sub="Every IP address in the return, with likely home and work networks and optional VPN / Tor screening." />
            <GeneratedBadge />
            <IpAnalysis events={events} tz={tz} fmt={fmt} tzLabel={tzLabel} exportName={`${input.title} IP analysis`} />
          </>
        )}
        {id === 'changes' && (
          <SecurityView rows={changes} fmt={fmt} tzLabel={tzLabel} title="Account changes"
            sub="Changes to the account's name, username, email, phone, password and status, in order, with the IP they came from."
            explain={`These are the moments the account's identity or access changed. When ${input.source} didn't log an IP with a change, the IP of the nearest activity within 2 hours is shown (marked ≈✱) as a lead.`}
            emptyText="No dated account changes in this return." />
        )}
        {id === 'summary' && (
          <CaseSummaryView scope={input.scope} busy={pdfBusy} onDownload={downloadSummary}
            explain="One document for the case file: the account details, top IP addresses with likely home and work networks, top contacts by days, conversations, people messaged who aren't connected, places, account changes, and the pattern-of-life heatmap with the longest gaps. Times follow the menu at the top of this page."
            counts={[
              ['Dated records', events.length.toLocaleString()],
              ['Contacts', contacts.length.toLocaleString()],
              ['Conversations', threads.length.toLocaleString()],
              ['Account changes', changes.length.toLocaleString()],
              ...(input.places ? [['GPS points', input.places.length.toLocaleString()]] : []),
            ]} />
        )}
        {!events.length && id !== 'summary' && <Note>No dated records were found in this return to analyze.</Note>}
      </div>
    </div>
  );
}
